import React, { useCallback, useEffect, useState } from 'react';

import { PaymentApi } from '@/services/api/paymentApi';
import type {
  PaymentCreateOrderResponse,
  PaymentMethod,
  PaymentOrderStatus,
  PaymentProduct,
} from '@/types';

import {
  type PaymentChannelAction,
  resolvePaymentChannelAction,
} from '../utils/paymentChannelPayload';
import PointsBuyModal from './PointsBuyModal';
import PointsPurchaseModal from './PointsPurchaseModal';

interface ExternalPointsBuyModalProps {
  open: boolean;
  onClose: () => void;
}

const readTheme = (): 'light' | 'dark' => {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
};

const executePaymentChannelAction = (action: PaymentChannelAction): void => {
  if (action.type === 'redirect') {
    window.open(action.url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (action.type === 'submit-form') {
    const container = document.createElement('div');
    container.style.display = 'none';
    container.innerHTML = action.html;
    document.body.appendChild(container);
    const form = container.querySelector('form');
    if (form) {
      form.setAttribute('target', '_blank');
      form.submit();
    }
    window.setTimeout(() => container.remove(), 1000);
  }
};

const notifyPointsSummaryInvalidated = (): void => {
  window.dispatchEvent(new Event('points:daily-access-recorded'));
};

const ExternalPointsBuyModal: React.FC<ExternalPointsBuyModalProps> = ({ open, onClose }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => readTheme());
  const [paymentProducts, setPaymentProducts] = useState<PaymentProduct[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [paymentProductsLoading, setPaymentProductsLoading] = useState(false);
  const [purchasingProductId, setPurchasingProductId] = useState<string | null>(null);
  const [paymentPurchaseOpen, setPaymentPurchaseOpen] = useState(false);
  const [paymentPurchaseOrder, setPaymentPurchaseOrder] =
    useState<PaymentCreateOrderResponse | null>(null);
  const [paymentPurchaseStatus, setPaymentPurchaseStatus] = useState<PaymentOrderStatus | null>(
    null,
  );
  const [paymentPurchaseChannelAction, setPaymentPurchaseChannelAction] =
    useState<PaymentChannelAction>({ type: 'none' });
  const [paymentPurchaseRefreshing, setPaymentPurchaseRefreshing] = useState(false);
  const [paymentPurchaseError, setPaymentPurchaseError] = useState<string | null>(null);

  const loadPaymentProducts = useCallback(async () => {
    setPaymentProductsLoading(true);
    setPaymentPurchaseError(null);
    try {
      const response = await PaymentApi.getProducts();
      setPaymentProducts(response.products);
      const nextPaymentMethods = response.payment_methods ?? [];
      setPaymentMethods(nextPaymentMethods);
      setSelectedPaymentMethodId((current) => {
        if (current && nextPaymentMethods.some((method) => method.method_id === current)) {
          return current;
        }
        return nextPaymentMethods[0]?.method_id ?? null;
      });
    } catch (error) {
      setPaymentProducts([]);
      setPaymentMethods([]);
      setSelectedPaymentMethodId(null);
      setPaymentPurchaseError(error instanceof Error ? error.message : '积分套餐加载失败');
    } finally {
      setPaymentProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTheme(readTheme());
    void loadPaymentProducts();
  }, [loadPaymentProducts, open]);

  const refreshPaymentPurchase = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!paymentPurchaseOrder) return;
      const silent = options.silent === true;
      if (!silent) {
        setPaymentPurchaseRefreshing(true);
      }
      setPaymentPurchaseError(null);
      try {
        const latest = await PaymentApi.refreshOrder(paymentPurchaseOrder.order_id);
        setPaymentPurchaseStatus(latest);
        if (latest.pay_status === 'PAID' && latest.credit_status === 'SUCCESS') {
          notifyPointsSummaryInvalidated();
        }
      } catch (error) {
        if (!silent) {
          setPaymentPurchaseError(error instanceof Error ? error.message : '支付状态刷新失败');
        }
      } finally {
        if (!silent) {
          setPaymentPurchaseRefreshing(false);
        }
      }
    },
    [paymentPurchaseOrder],
  );

  const handlePurchaseProduct = useCallback(async (product: PaymentProduct, methodId: string) => {
    setPurchasingProductId(product.product_id);
    setPaymentPurchaseError(null);
    try {
      const order = await PaymentApi.createOrder({
        productId: product.product_id,
        paymentMethodId: methodId,
      });
      const channelAction = resolvePaymentChannelAction(
        order.channel_return_body,
        order.payment_method,
      );
      setPaymentPurchaseOrder(order);
      setPaymentPurchaseStatus(null);
      setPaymentPurchaseChannelAction(channelAction);
      setPaymentPurchaseOpen(true);
      executePaymentChannelAction(channelAction);
      window.setTimeout(() => {
        void PaymentApi.refreshOrder(order.order_id).then((latest) => {
          setPaymentPurchaseStatus(latest);
          if (latest.pay_status === 'PAID' && latest.credit_status === 'SUCCESS') {
            notifyPointsSummaryInvalidated();
          }
        });
      }, 1500);
    } catch (error) {
      setPaymentPurchaseError(error instanceof Error ? error.message : '支付订单创建失败');
    } finally {
      setPurchasingProductId(null);
    }
  }, []);

  useEffect(() => {
    if (!paymentPurchaseOpen || !paymentPurchaseOrder) return undefined;
    if (
      paymentPurchaseStatus?.pay_status === 'PAID' &&
      paymentPurchaseStatus.credit_status === 'SUCCESS'
    ) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void refreshPaymentPurchase({ silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [
    paymentPurchaseOpen,
    paymentPurchaseOrder,
    paymentPurchaseStatus?.credit_status,
    paymentPurchaseStatus?.pay_status,
    refreshPaymentPurchase,
  ]);

  return (
    <>
      <PointsBuyModal
        open={open}
        onClose={onClose}
        theme={theme}
        paymentProducts={paymentProducts}
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelectPaymentMethod={setSelectedPaymentMethodId}
        paymentProductsLoading={paymentProductsLoading}
        purchasingProductId={purchasingProductId}
        paymentPurchaseError={paymentPurchaseError}
        onPurchaseProduct={handlePurchaseProduct}
      />
      {paymentPurchaseOrder ? (
        <PointsPurchaseModal
          open={paymentPurchaseOpen}
          order={paymentPurchaseOrder}
          status={paymentPurchaseStatus}
          refreshing={paymentPurchaseRefreshing}
          error={paymentPurchaseError}
          channelAction={paymentPurchaseChannelAction}
          onRefresh={() => {
            void refreshPaymentPurchase();
          }}
          onClose={() => setPaymentPurchaseOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ExternalPointsBuyModal;
